import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Modal,
  FormV3 as Form,
  Row,
  Col,
  Table,
  Notification,
} from '~/components';
import { fileManagerPaiDownload } from '~/services/FileManager/file-manager.service';
import { obterAnexos } from '~/modules/Modulos/services/arquivos.service';
import {
  obterChaveArquivo,
  marcarDownloadAtivo,
  desmarcarDownloadAtivo,
  criarAtualizadorProgresso,
  ehAborto,
  abrirNovaAba,
} from '~/modules/Modulos/helpers/arquivos.helper';
import ButtonLinkFileManagerLinkAnexo from './ButtonLinkFileManager';
import ButtonDownloadFileManager from './ButtonDowloadFileManager';
import ButtonLinkAnexo from './ButtonLinkAnexo';
import AnexoDownloadProgresso from './anexoDownloadProgresso.component';
import { TabelaAnexos, AnexoCelula } from './arquivos.form.styled';

const MENSAGEM_POPUP_BLOQUEADO =
  'O navegador bloqueou a abertura do arquivo. Permita pop-ups para este site e tente novamente.';

function ArquivosForm({
  visible,
  onCancel,
  chaveModulo,
  filtroPesquisaArquivosDigitais,
}) {
  const [arquivosAnexados, setArquivosAnexados] = useState([]);
  const [downloads, setDownloads] = useState({});
  const montadoRef = useRef(true);
  const corTema = useSelector(state => state.configuration.corTema);
  const titulo = 'Listagem dos Anexos';

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  useEffect(() => {
    async function buscarAnexos() {
      if (filtroPesquisaArquivosDigitais) {
        const response = await obterAnexos(filtroPesquisaArquivosDigitais);
        setArquivosAnexados(response);
      }
    }
    if (visible) buscarAnexos();
  }, [chaveModulo, filtroPesquisaArquivosDigitais, visible]);

  const onCloseModal = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const avisarPopupBloqueado = useCallback(() => {
    if (montadoRef.current) {
      Notification.showWarning(MENSAGEM_POPUP_BLOQUEADO);
    }
  }, []);

  const baixarArquivo = useCallback(async record => {
    if (record && record.IsLinkAnexo) {
      if (!abrirNovaAba(record.LinkAnexo)) {
        avisarPopupBloqueado();
      }
      return;
    }

    const chave = obterChaveArquivo(record);
    if (!marcarDownloadAtivo(chave)) {
      return;
    }

    if (montadoRef.current) {
      setDownloads(atual => ({
        ...atual,
        [chave]: { recebido: 0, total: 0 },
      }));
    }

    const atualizarProgresso = criarAtualizadorProgresso(progresso => {
      if (!montadoRef.current) {
        return;
      }
      setDownloads(atual => ({
        ...atual,
        [chave]: progresso,
      }));
    });

    try {
      await fileManagerPaiDownload(record.UrlArquivoDigital, {
        onProgress: atualizarProgresso,
        onPopupBloqueado: () => {
          if (montadoRef.current) {
            Notification.showWarning(
              `${MENSAGEM_POPUP_BLOQUEADO} O arquivo será baixado nesta página.`
            );
          }
        },
      });
    } catch (erro) {
      if (!ehAborto(erro) && montadoRef.current) {
        Notification.showError(
          'Não foi possível baixar o arquivo. Tente novamente.'
        );
      }
    } finally {
      desmarcarDownloadAtivo(chave);
      if (montadoRef.current) {
        setDownloads(atual => {
          const proximo = { ...atual };
          delete proximo[chave];
          return proximo;
        });
      }
    }
  }, [avisarPopupBloqueado]);

  function showAnexos() {
    const columns = [
      {
        title: 'Anexos',
        dataIndex: 'NomeArquivoDigital',
        key: 'NomeArquivoDigital',
        render: (text, record) => {
          const chave = obterChaveArquivo(record);
          const progresso = downloads[chave];
          const baixando = Boolean(progresso);

          return {
            children: (
              <AnexoCelula>
                <ButtonLinkFileManagerLinkAnexo
                  key={record.IdArquivoDigital}
                  disabled={baixando}
                  onClick={() => {
                    if (baixando) {
                      return;
                    }
                    baixarArquivo(record);
                  }}
                >
                  {text}
                </ButtonLinkFileManagerLinkAnexo>
                <AnexoDownloadProgresso
                  progresso={progresso}
                  corTema={corTema}
                />
              </AnexoCelula>
            ),
            props: {
              'data-title': 'Anexos',
            },
          };
        },
      },
      {
        title: 'Ações',
        dataIndex: 'Acao',
        align: 'center',
        key: 'Acao',
        width: 100,
        render: (text, record) => {
          const chave = obterChaveArquivo(record);
          const baixando = Boolean(downloads[chave]);

          return {
            children: record?.IsLinkAnexo ? (
              <ButtonLinkAnexo
                key={record.IdArquivoDigital}
                aria-label={`Abrir link ${record.NomeArquivoDigital}`}
                onClick={() => {
                  if (!abrirNovaAba(record.LinkAnexo)) {
                    avisarPopupBloqueado();
                  }
                }}
              />
            ) : (
              <ButtonDownloadFileManager
                key={record.IdArquivoDigital}
                loading={baixando}
                aria-label={`Baixar ${record.NomeArquivoDigital}`}
                onClick={() => {
                  if (baixando) {
                    return;
                  }
                  baixarArquivo(record);
                }}
              />
            ),
            props: {
              'data-title': 'Ações',
            },
          };
        },
      },
    ];

    return (
      <TabelaAnexos>
        <Table
          bordered
          caption={titulo || 'Anexos do registro'}
          dataSource={arquivosAnexados}
          rowKey={row => row.IdArquivoDigital || row.NomeArquivoDigital}
          rowClassName={record =>
            downloads[obterChaveArquivo(record)] ? 'anexo-baixando' : ''
          }
          pagination={false}
          scroll={{ x: 650 }}
          onRow={record => {
            const baixando = Boolean(downloads[obterChaveArquivo(record)]);
            return {
              'aria-busy': baixando || undefined,
            };
          }}
          columns={columns}
          locale={{ emptyText: 'Não constam anexos para este registro.' }}
        />
      </TabelaAnexos>
    );
  }

  return (
    <Modal title={titulo} width={750} visible={visible} onCancel={onCloseModal}>
      <Form>
        <Row gutter={8}>
          <Col span={24}>{showAnexos()}</Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default ArquivosForm;
